import { type PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getAllClients } from "../service/client.service";
import type { TClientForm, Tdsm } from "../types/index.type";
import { sortClientsByName } from "../utils/datamanipulation.utils";

// Async thunk to fetch all clients
export const fetchAllClients = createAsyncThunk("client/fetchAllClients", async (_, { rejectWithValue }) => {
  try {
    const response = await getAllClients();
    const ownedClients = sortClientsByName(response.data.owned || []);
    const sharedClients = sortClientsByName(response.data.shared || []);
    return { ownedClients, sharedClients };
  } catch (error) {
    return rejectWithValue(error);
  }
});

// Define a type for the slice state
type ClientState = {
  clients: TClientForm[];
  sharedClients: TClientForm[];
  filteredOwnedClients: TClientForm[];
  filteredSharedClients: TClientForm[];
  clientForm: TClientForm;
  dsm: Tdsm[];
  customizationList: any[];
  note_type_instructions: {
    [key: number]: string | { note: string; is_system: boolean };
  };
  suggestion: string;
  isEditing: boolean;
};

// Initial state
const initialState: ClientState = {
  clients: [],
  sharedClients: [],
  filteredOwnedClients: [],
  filteredSharedClients: [],
  dsm: [],
  clientForm: {
    name: "",
    category_type: 0,
    gender_type: 0,
    language_type: 0,
    diagnosis_ids: [],
    refer_type: 0,
    mrn: "",
    date_of_birth: null,
    context: "",
  },
  customizationList: [],
  note_type_instructions: {},
  suggestion: "",
  isEditing: false,
};

// Slice
const clientSlice = createSlice({
  name: "client",
  initialState,
  reducers: {
    setClients: (state, action: PayloadAction<TClientForm[]>) => {
      state.clients = action.payload;
    },
    setSharedClients: (state, action: PayloadAction<TClientForm[]>) => {
      state.sharedClients = action.payload;
    },
    setFilteredOwnedClients: (state, action: PayloadAction<TClientForm[]>) => {
      state.filteredOwnedClients = action.payload;
    },
    setFilteredSharedClients: (state, action: PayloadAction<TClientForm[]>) => {
      state.filteredSharedClients = action.payload;
    },
    setClientForm: (state, action: PayloadAction<TClientForm>) => {
      state.clientForm = action.payload;
    },
    setDsm: (state, action: PayloadAction<{ id: number; code: string; name: string }[]>) => {
      state.dsm = action.payload;
    },
    setCustomizationList: (state, action: PayloadAction<any[]>) => {
      state.customizationList = action.payload;
    },
    setNoteTypeInstructionNote: (
      state,
      action: PayloadAction<{
        [key: number]: string | { note: string; is_system: boolean };
      }>,
    ) => {
      state.note_type_instructions = action.payload;
    },
    setIsEditing: (state, action: PayloadAction<boolean>) => {
      state.isEditing = action.payload;
    },
    resetClientForm: (state) => {
      state.clientForm = { ...initialState.clientForm };
    },
    setSuggestion: (state, action: PayloadAction<string>) => {
      state.suggestion = action.payload;
    },
    clearSuggestion: (state) => {
      state.suggestion = "";
    },
    addClient: (state, action: PayloadAction<TClientForm>) => {
      // Add new client and re-sort all relevant lists
      const newClient = action.payload;
      state.clients = sortClientsByName([...state.clients, newClient]);
      state.filteredOwnedClients = sortClientsByName([...state.filteredOwnedClients, newClient]);
    },
    removeClient: (state, action: PayloadAction<number>) => {
      // Remove client by ID from all relevant lists
      const clientId = action.payload;
      state.clients = state.clients.filter((client) => client.id !== clientId);
      state.sharedClients = state.sharedClients.filter((client) => client.id !== clientId);
      state.filteredOwnedClients = state.filteredOwnedClients.filter((client) => client.id !== clientId);
      state.filteredSharedClients = state.filteredSharedClients.filter((client) => client.id !== clientId);
    },
    updateClientInList: (state, action: PayloadAction<TClientForm>) => {
      // Update a client in all relevant lists
      const updatedClient = action.payload;
      const updateInList = (list: TClientForm[]) => {
        const index = list.findIndex((client) => client.id === updatedClient.id);
        if (index !== -1) {
          list[index] = updatedClient;
        }
        return sortClientsByName(list);
      };
      state.clients = updateInList([...state.clients]);
      state.filteredOwnedClients = updateInList([...state.filteredOwnedClients]);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAllClients.fulfilled, (state, action) => {
      const { ownedClients, sharedClients } = action.payload;
      state.clients = [...ownedClients, ...sharedClients];
      state.sharedClients = sharedClients;
    });
  },
});

// Actions
export const {
  setClientForm,
  setClients,
  setSharedClients,
  setFilteredOwnedClients,
  setFilteredSharedClients,
  resetClientForm,
  setDsm,
  setIsEditing,
  setNoteTypeInstructionNote,
  setCustomizationList,
  setSuggestion,
  clearSuggestion,
  addClient,
  removeClient,
  updateClientInList,
} = clientSlice.actions;

// Reducer
export default clientSlice.reducer;
